import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ShieldCheck, UserPlus, ArrowLeft, FileText, LifeBuoy } from "lucide-react";
import { getActiveLang } from "../lib/activeLang";
import { t } from "../lib/ui";

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_URL = "https://wa.me/393341148412";
const WARRANTY_PATH = "/assistenza/garanzia";
const YELLOW = "#F9E212";

function newSessionId() {
  try {
    const k = "torri_chat_sid";
    let sid = localStorage.getItem(k);
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) || `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(k, sid);
    }
    return sid;
  } catch {
    return `sid-${Date.now()}`;
  }
}

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M17.47 14.38c-.29-.15-1.71-.84-1.98-.94-.27-.1-.46-.15-.65.15-.19.29-.75.94-.92 1.13-.17.19-.34.22-.63.07-.29-.15-1.22-.45-2.32-1.44-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.65-1.57-.89-2.15-.24-.57-.48-.49-.65-.5l-.56-.01c-.19 0-.51.07-.77.36-.27.29-1.01.99-1.01 2.42 0 1.43 1.04 2.81 1.19 3 .15.19 2.05 3.13 4.97 4.39.69.3 1.23.48 1.65.61.69.22 1.32.19 1.82.12.56-.08 1.71-.7 1.95-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.79-1.31l-.34-.2-3.56.93.95-3.47-.22-.36a9.38 9.38 0 0 1-1.44-5.01c0-5.18 4.22-9.4 9.41-9.4 2.51 0 4.87.98 6.64 2.76a9.34 9.34 0 0 1 2.75 6.65c-.01 5.18-4.22 9.41-9.4 9.41zM20.52 3.49A11.77 11.77 0 0 0 12.05 0C5.5 0 .17 5.33.17 11.89c0 2.1.55 4.14 1.59 5.95L.07 24l6.33-1.66a11.86 11.86 0 0 0 5.65 1.44h.01c6.55 0 11.88-5.33 11.88-11.89 0-3.18-1.24-6.16-3.42-8.4z" />
  </svg>
);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "lead"
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", note: "" });
  const [leadStatus, setLeadStatus] = useState(null); // null | "ok" | "err" | "sending"
  const scrollRef = useRef(null);
  const lang = getActiveLang();

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat_welcome") }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, view]);

  const patchLast = (patch) =>
    setMessages((m) => {
      if (m.length === 0) return m;
      const copy = [...m];
      const i = copy.length - 1;
      copy[i] = { ...copy[i], ...(typeof patch === "function" ? patch(copy[i]) : patch) };
      return copy;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", sources: [], streaming: true }]);
    setSending(true);
    try {
      const clientToken = localStorage.getItem("torri_client_token") || "";
      const headers = { "Content-Type": "application/json" };
      if (clientToken) headers.Authorization = `Bearer ${clientToken}`;
      const res = await fetch(`${API}/api/chat/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({ session_id: sessionId, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        const events = sseBuf.split("\n\n");
        sseBuf = events.pop();
        for (const evt of events) {
          let eventType = "message";
          let dataStr = "";
          for (const line of evt.split("\n")) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let payload;
          try { payload = JSON.parse(dataStr); } catch { continue; }
          if (eventType === "meta") {
            patchLast({ sources: payload.sources || [] });
          } else if (eventType === "done") {
            patchLast({ streaming: false });
          } else if (payload.delta) {
            patchLast((last) => ({ content: (last.content || "") + payload.delta }));
          }
        }
      }
      patchLast({ streaming: false });
    } catch {
      patchLast({ content: t("chat_error"), streaming: false });
    } finally {
      setSending(false);
    }
  };

  const showSupportInfo = () => {
    setMessages((m) => [...m, { role: "assistant", content: t("chat_support_intro"), showLogin: true }]);
  };

  const sourceHref = (s) => {
    const token = localStorage.getItem("torri_client_token") || "";
    if (s.visibility === "public") return `${API}/api/documents/${s.doc_id}/public-download`;
    return `${API}/api/documents/${s.doc_id}/download?auth=${encodeURIComponent(token)}`;
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim()) return;
    setLeadStatus("sending");
    try {
      const res = await fetch(`${API}/api/chat/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...lead, lang, session_id: sessionId }),
      });
      if (!res.ok) throw new Error("bad");
      setLeadStatus("ok");
      setLead({ name: "", email: "", phone: "", note: "" });
      setTimeout(() => { setView("chat"); setLeadStatus(null); }, 1800);
    } catch {
      setLeadStatus("err");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          aria-label={t("chat_open")}
          className="fixed bottom-5 right-5 z-[1000] flex items-center gap-2 rounded-full pl-4 pr-5 py-3 shadow-2xl transition-transform duration-200 hover:scale-105"
          style={{ background: "#111", color: "#fff", fontFamily: "Space Grotesk" }}
        >
          <span className="grid place-items-center w-8 h-8 rounded-full" style={{ background: YELLOW, color: "#111" }}>
            <MessageCircle size={18} />
          </span>
          <span className="text-sm font-bold tracking-wide">{t("chat_open")}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-0 z-[1000] flex flex-col overflow-hidden bg-white shadow-2xl h-[100dvh] sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[380px] sm:h-[600px] sm:max-h-[calc(100vh-40px)] sm:rounded-2xl sm:border sm:border-black/10"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: "#111", color: "#fff" }}>
            {view === "lead" && (
              <button onClick={() => setView("chat")} data-testid="chat-lead-back" className="opacity-80 hover:opacity-100" aria-label="Back">
                <ArrowLeft size={18} />
              </button>
            )}
            <span className="grid place-items-center w-9 h-9 rounded-full shrink-0" style={{ background: YELLOW, color: "#111" }}>
              <MessageCircle size={18} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-tight" style={{ fontFamily: "Space Grotesk" }}>{t("chat_title")}</div>
              <div className="text-[11px] text-white/60 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#31d158" }} />
                {t("chat_subtitle")}
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="opacity-80 hover:opacity-100" aria-label="Close">
              <X size={20} />
            </button>
          </div>

          {view === "chat" ? (
            <>
              {/* Messages */}
              <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "#F5F5F3" }}>
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[82%] flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                      <div
                        className="px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words"
                        style={
                          m.role === "user"
                            ? { background: "#111", color: "#fff", borderRadius: "14px 14px 4px 14px", fontFamily: "Inter" }
                            : { background: "#fff", color: "#111", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "14px 14px 14px 4px", fontFamily: "Inter" }
                        }
                      >
                        {m.role === "assistant" && m.streaming && !m.content ? (
                          <span className="flex gap-1.5 py-1" data-testid="chat-typing">
                            <span className="w-2 h-2 rounded-full bg-black/30 animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-2 h-2 rounded-full bg-black/30 animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-2 h-2 rounded-full bg-black/30 animate-bounce" style={{ animationDelay: "300ms" }} />
                          </span>
                        ) : (
                          <>
                            {m.content}
                            {m.role === "assistant" && m.streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-black/50 animate-pulse" />}
                          </>
                        )}
                      </div>
                      {m.role === "assistant" && m.showLogin && (
                        <a
                          href="/area-clienti"
                          data-testid="chat-login-cta"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full transition-transform hover:scale-[1.03]"
                          style={{ background: YELLOW, color: "#111", fontFamily: "Space Grotesk" }}
                        >
                          <ShieldCheck size={13} /> {t("chat_login_cta")}
                        </a>
                      )}
                      {m.role === "assistant" && m.sources && m.sources.length > 0 && (
                        <div className="mt-2 w-full" data-testid="chat-sources">
                          <div className="text-[11px] uppercase tracking-[0.12em] text-black/60 mb-1" style={{ fontFamily: "Space Grotesk" }}>{t("chat_sources")}</div>
                          <div className="flex flex-col gap-1.5">
                            {m.sources.map((s) => (
                              <a
                                key={s.doc_id}
                                href={sourceHref(s)}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`chat-source-${s.doc_id}`}
                                className="inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border border-black/10 bg-white hover:border-black/40 transition-colors"
                                style={{ fontFamily: "Inter" }}
                                title={s.filename}
                              >
                                <FileText size={13} className="shrink-0" style={{ color: "#b58a00" }} />
                                <span className="truncate max-w-[240px]">{s.filename}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2 px-3 pt-2.5 bg-white border-t border-black/5">
                <button
                  onClick={showSupportInfo}
                  data-testid="chat-qa-support"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-black/15 hover:bg-black hover:text-white transition-colors"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  <LifeBuoy size={13} /> {t("chat_qa_support")}
                </button>
                <a
                  href={WARRANTY_PATH}
                  data-testid="chat-qa-warranty"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-black/15 hover:bg-black hover:text-white transition-colors"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  <ShieldCheck size={13} /> {t("chat_qa_warranty")}
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="chat-qa-whatsapp"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full text-white transition-opacity hover:opacity-90"
                  style={{ background: "#25D366", fontFamily: "Space Grotesk" }}
                >
                  <WhatsAppIcon /> {t("chat_qa_whatsapp")}
                </a>
                <button
                  onClick={() => setView("lead")}
                  data-testid="chat-qa-lead"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-black/15 hover:bg-black hover:text-white transition-colors"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  <UserPlus size={13} /> {t("chat_qa_lead")}
                </button>
              </div>

              {/* Input */}
              <div className="flex items-end gap-2 p-3 bg-white border-t border-black/5">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  data-testid="chat-input"
                  rows={1}
                  placeholder={t("chat_placeholder")}
                  className="flex-1 resize-none max-h-24 text-base px-3 py-2.5 rounded-xl border border-black/15 focus:outline-none focus:border-black/40"
                  style={{ fontFamily: "Inter", fontSize: "16px" }}
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  data-testid="chat-send"
                  className="grid place-items-center w-11 h-11 rounded-xl shrink-0 disabled:opacity-40 transition-transform hover:scale-105"
                  style={{ background: YELLOW, color: "#111" }}
                  aria-label={t("chat_send")}
                >
                  <Send size={18} />
                </button>
              </div>
            </>
          ) : (
            /* Lead form */
            <div className="flex-1 overflow-y-auto p-5 bg-white">
              <h3 className="text-lg font-black" style={{ fontFamily: "Archivo" }}>{t("chat_lead_title")}</h3>
              <p className="text-sm text-black/60 mt-1 mb-4" style={{ fontFamily: "Inter" }}>{t("chat_lead_desc")}</p>
              {leadStatus === "ok" ? (
                <div data-testid="chat-lead-success" className="rounded-xl border border-green-200 bg-green-50 text-green-800 text-sm p-4" style={{ fontFamily: "Inter" }}>
                  {t("chat_lead_ok")}
                </div>
              ) : (
                <form onSubmit={submitLead} className="space-y-3" data-testid="chat-lead-form">
                  <input
                    required
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    placeholder={t("chat_lead_name")}
                    data-testid="chat-lead-name"
                    className="w-full text-base px-3 py-2.5 rounded-xl border border-black/15 focus:outline-none focus:border-black/40"
                    style={{ fontFamily: "Inter", fontSize: "16px" }}
                  />
                  <input
                    required
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    placeholder={t("chat_lead_email")}
                    data-testid="chat-lead-email"
                    className="w-full text-base px-3 py-2.5 rounded-xl border border-black/15 focus:outline-none focus:border-black/40"
                    style={{ fontFamily: "Inter", fontSize: "16px" }}
                  />
                  <input
                    value={lead.phone}
                    onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                    placeholder={t("chat_lead_phone")}
                    data-testid="chat-lead-phone"
                    className="w-full text-base px-3 py-2.5 rounded-xl border border-black/15 focus:outline-none focus:border-black/40"
                    style={{ fontFamily: "Inter", fontSize: "16px" }}
                  />
                  <textarea
                    value={lead.note}
                    onChange={(e) => setLead({ ...lead, note: e.target.value })}
                    placeholder={t("chat_lead_note")}
                    rows={3}
                    data-testid="chat-lead-note"
                    className="w-full text-base px-3 py-2.5 rounded-xl border border-black/15 resize-none focus:outline-none focus:border-black/40"
                    style={{ fontFamily: "Inter", fontSize: "16px" }}
                  />
                  {leadStatus === "err" && (
                    <p className="text-sm text-red-600" style={{ fontFamily: "Inter" }}>{t("chat_lead_err")}</p>
                  )}
                  <button
                    type="submit"
                    disabled={leadStatus === "sending"}
                    data-testid="chat-lead-submit"
                    className="w-full py-3 rounded-xl font-bold text-sm disabled:opacity-50 transition-transform hover:scale-[1.02]"
                    style={{ background: YELLOW, color: "#111", fontFamily: "Space Grotesk" }}
                  >
                    {t("chat_lead_submit")}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
